import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CollapseComponent } from '@d-components/animations/collapse.component';
import { BaseButtonComponent } from '@d-components/button/button.component';
import { BaseFormComponent } from '@d-components/form/form.component';
import { FormConfig, OptionsModel } from '@d-components/form/form.interface';
import { FilterListingService } from '@d-services/wms/filter-listing.service';
import {
  MODULE_CODE_PERMISSION,
  ACTION_PERMISSION,
} from '@d-constants/permission.constant';
import { RoleService } from '@d-services';
import { TranslateModule } from '@ngx-translate/core';
import { ListingFilterDirective } from 'app/core/directives/listing-filter.directive';
import { reduce, size } from 'lodash-es';
import { ButtonModule } from 'primeng/button';
import { Dropdown, DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-base-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownModule,
    InputTextModule,
    BaseButtonComponent,
    BaseFormComponent,
    ButtonModule,
    CollapseComponent,
    TranslateModule,
    ListingFilterDirective,
  ],
})
export class BaseSearchComponent implements OnInit, OnChanges {
  @Input() showSearchGlobal: boolean = true;
  @Input() advancedSearchConfig: FormConfig[] = [];
  @Input() searchOptions: OptionsModel[] = [];
  @Input() searchString: string = '';
  @Input() isDisplaySearchOptions: boolean = true;
  @Input() defaultFilterBy: string = '';
  @Input() placeholder = 'Search by';
  @Input() maxlength: number = 50;
  @Input() optionsSorter?: (a: OptionsModel, b: OptionsModel) => number;
  @Input() advanceSearch: boolean = true;
  @Input() advancedSearchToggleMode: 'button' | 'text' = 'button';
  @Input() hideTitle = false;
  @Input() triggerOnSearchFlag = false;
  @Output() onClearFilter = new EventEmitter();
  @Output() onSearch = new EventEmitter();
  @Input() isCollapsedAdvancedFilter = false;
  @Input() listingFilter;
  @Input() searchGUID: boolean = true;
  @Input() eventsChangePortal?: Observable<void>;
  private eventsSubscription?: Subscription;
  @Input() disableSearchOption: boolean = false;

  searchBy = new FormControl(this.searchString);
  filterBy = new FormControl(this.defaultFilterBy || 'All');
  selectedSearchOption: any;

  constructor(
    private route: ActivatedRoute,
    private filterService: FilterListingService,
    private roleService: RoleService,
  ) {}
  ngOnInit(): void {
    this.overridePrimeNG();
    const firstField = this.advancedSearchConfig.find(
      (i) => i.fieldControl,
    )?.fieldControl;
    this.route.queryParams.subscribe((params) => {
      if (size(params)) {
        const fieldValues = reduce(
          this.route.snapshot.queryParams,
          (result, value, key) => {
            result[key] = isNaN(value) ? value : parseInt(value);
            return result;
          },
          {},
        );
        if (firstField) {
          firstField?.parent?.patchValue(fieldValues as any);
          if (
            Object.keys(firstField?.parent?.controls)?.some((value) =>
              Object.keys(fieldValues)?.includes(value),
            )
          )
            this.onSearch.emit({
              advancedFilter: firstField?.parent?.getRawValue(),
            });
        }
      }
    });
    if (this.eventsChangePortal) {
      this.eventsSubscription = this.eventsChangePortal.subscribe(() =>
        this.reset(),
      );
    }
  }

  ngOnDestroy() {
    if (this.eventsChangePortal) {
      this.eventsSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('searchOptions')) {
      if (
        this.filterService.locationName === this.listingFilter &&
        this.filterService.filterData
      ) {
        const { searchGlobal } = this.filterService.filterData;
        if (searchGlobal) {
          this.searchBy.setValue(searchGlobal.searchBy);
          this.filterBy.setValue(searchGlobal.filterBy);
          this.defaultFilterBy = searchGlobal.filterBy;
          this.onSearch.emit({
            searchGlobal,
          });
        }
      }
      if (this.searchGUID && this.searchOptions) {
        const hasPermission = this.roleService.checkPermissionByAction(
          MODULE_CODE_PERMISSION.SPECIAL_ACCESS.Subsidy_OP_SPECIAL_ACCESS,
          ACTION_PERMISSION.READ,
        );
        if (hasPermission)
          this.searchOptions.splice(0, 0, {
            value: 'GUID',
            label: 'GUID',
          });
      }

      if (this.defaultFilterBy) {
        this.selectedSearchOption = this.defaultFilterBy;
      } else {
        this.selectedSearchOption = this.searchOptions[0];
      }
      // this.searchOptions = this.searchOptions.sort(
      //   this.optionsSorter ||
      //     ((a: any, b: any) =>
      //       a.label?.toLowerCase().localeCompare(b.label?.toLowerCase()))
      // );
    }

    if (
      changes.hasOwnProperty('disableSearchOption') &&
      this.disableSearchOption
    ) {
      this.searchBy.disable();
      this.filterBy.disable();
    }
    if (changes.hasOwnProperty('triggerOnSearchFlag')) {
      this.onSearch.emit({
        searchGlobal: {
          searchBy: this.searchBy.value,
          filterBy: this.filterBy.value,
        },
      });
    }

    if (changes.hasOwnProperty('advancedSearchConfig')) {
      if (
        this.advancedSearchConfig.find(
          (item) => item.label === 'APPROVAL_STATUS',
        )
      ) {
        this.route.queryParams.subscribe((params) => {
          if (size(params)) {
            const firstField = this.advancedSearchConfig.find(
              (i) => i.fieldControl,
            )?.fieldControl;
            firstField?.parent?.patchValue(
              reduce(
                this.route.snapshot.queryParams,
                (result, value, key) => {
                  result[key] = Number.isNaN(value) ? value : parseInt(value);
                  return result;
                },
                {},
              ) as any,
            );
            this.onSearch.emit({
              advancedFilter: firstField?.parent?.getRawValue(),
            });
          }
        });
      }

      if (
        this.filterService.locationName === this.listingFilter &&
        this.filterService.filterData
      ) {
        const { searchGlobal, advancedFilter } = this.filterService.filterData;
        if (searchGlobal) {
          this.searchBy.setValue(searchGlobal.searchBy);
          this.filterBy.setValue(searchGlobal.filterBy);
          this.onSearch.emit({
            searchGlobal: searchGlobal,
          });
        } else if (advancedFilter) {
          const firstField = this.advancedSearchConfig.find(
            (i) => i.fieldControl,
          )?.fieldControl;

          if (firstField) {
            firstField?.parent?.patchValue(advancedFilter);
            this.isCollapsedAdvancedFilter = true;
            this.onSearch.emit({
              advancedFilter: firstField?.parent?.getRawValue(),
            });
          }
        }
      }
    }
  }

  onKeydown($event: KeyboardEvent) {
    if ($event.key === 'Enter') {
      this.onSearch.emit({
        searchGlobal: {
          searchBy: this.searchBy.value,
          filterBy: this.filterBy.value,
        },
      });

      this.filterService.filterData = {
        searchGlobal: {
          searchBy: this.searchBy.value,
          filterBy: this.filterBy.value,
        },
        advancedFilter: null,
      };
    }
  }

  handleClearFilter() {
    const firstField = this.advancedSearchConfig.find(
      (i) => i.fieldControl,
    )?.fieldControl;
    firstField?.parent?.reset();
    this.onClearFilter.emit({
      searchGlobal: {
        searchBy: this.searchBy.value,
        filterBy: this.filterBy.value,
      },
      advancedFilter: firstField?.parent?.value,
    });
  }

  reset() {
    this.searchBy.reset();
  }

  onSearchClick() {
    const firstField = this.advancedSearchConfig.find(
      (i) => i.fieldControl,
    )?.fieldControl;

    this.onSearch.emit({
      advancedFilter: this.isCollapsedAdvancedFilter
        ? firstField?.parent?.getRawValue()
        : null,
    });
    this.filterService.filterData = {
      searchGlobal: null,
      advancedFilter: this.isCollapsedAdvancedFilter
        ? firstField?.parent?.getRawValue()
        : null,
    };
  }

  getLabelByValue(value: any) {
    return this.searchOptions.find((i) => i.value === value)?.label || '';
  }

  private overridePrimeNG() {
    let clonedFunc = Dropdown.prototype.onKeydown;
    Dropdown.prototype.onKeydown = function (event, search) {
      if (
        event.key === 'Enter' &&
        this.name === 'global' &&
        !this.overlayVisible
      ) {
        return;
      }
      clonedFunc.call(this, event, search);
    };
  }
}
